/**
 * Global VideoStudio command handlers.
 *
 * These are registered once at app startup (in commandRegistryStore.initializeAllCommands)
 * so that a tool-runtime handler's `ctx.broadcastCmd('video_studio_progress', ...)`
 * or `ctx.broadcastCmd('video_studio_result', ...)` lands here — identical shape
 * to the Image Studio broadcast contract.
 *
 * Handlers update the ingest queue store (Phase B/C) and, when applicable,
 * apply the AI job status to the owning VideoProject's Y.Doc so any open
 * VideoStudio window sees the update live.
 */

import { useIngestQueueStore } from '../services/ingestQueueStore';
import { vsLogJobs } from '../services/videoStudioLogger';
import type { VideoStudioProgressEvent, VideoStudioResultEvent } from '../types';

export const videoStudioProgressCommand = {
  id: 'video_studio_progress',
  description: 'Receive VideoStudio AI-op progress broadcast',
  handler: async (params: VideoStudioProgressEvent) => {
    if (!params?.opId) {
      return { status: 'skipped', reason: 'missing opId' };
    }
    vsLogJobs.log('[cmd] video_studio_progress', {
      opId: params.opId,
      jobId: params.jobId,
      status: params.status,
      stage: params.stage,
      pct: params.pct,
    });
    useIngestQueueStore.getState().upsert({
      opId: params.opId,
      status:
        params.status === 'succeeded'
          ? 'succeeded'
          : params.status === 'failed'
            ? 'failed'
            : 'running',
      stage: params.stage,
      pct: params.pct,
      label: params.message,
      sourceEntityId: params.sourceEntityId,
      spaceId: params.spaceId,
      error: params.error,
    });
    return { status: 'success' };
  },
};

export const videoStudioResultCommand = {
  id: 'video_studio_result',
  description: 'Receive VideoStudio AI-op result broadcast',
  handler: async (params: VideoStudioResultEvent) => {
    if (!params?.opId || !params?.resultEntityId) {
      return { status: 'skipped', reason: 'missing opId or resultEntityId' };
    }
    vsLogJobs.log('[cmd] video_studio_result', {
      opId: params.opId,
      jobId: params.jobId,
      mode: params.mode,
      resultEntityId: params.resultEntityId,
      suggestedReplace: params.suggestedReplace,
    });
    useIngestQueueStore.getState().upsert({
      opId: params.opId,
      status: 'succeeded',
      stage: 'persist',
      pct: 1,
      label: `${params.mode} → ${params.resultEntityId}`,
      spaceId: params.spaceId,
    });

    // TODO (Phase F): reach into the owning VideoProject's Y.Doc and mark the
    // matching aiJob row as 'succeeded' with resultEntityId, then (if
    // suggestedReplace) auto-splice the new clip into the timeline.
    return { status: 'success' };
  },
};

export const VIDEO_STUDIO_COMMANDS = [
  videoStudioProgressCommand,
  videoStudioResultCommand,
];
